import {
  DISABLE_LOADER,
  ENABLE_LOADER,
  SNACK_NOTIFICATION,
} from "pages/constants/EventKeys";
import { eventDispatcher } from "pages/Hooks/useEventListener";

type HttpMethod = "GET" | "POST" | "PUT" | "DELETE";

type RequestCredentials = "include" | "omit" | "same-origin";
interface RequestOptions {
  method: HttpMethod;
  headers?: Record<string, string>;
  body?: any;
  credentials?: RequestCredentials;
}

export type ErrorResponse = {
  status: string;
  message: string;
  error: any;
};

class HttpClient {
  private apiHost: string;

  constructor(apiHost: string) {
    this.apiHost = apiHost;
  }

  private async request<T>(
    url: string,
    type: string,
    options: RequestOptions
  ): Promise<T | ErrorResponse> {
    if (options.method !== "GET") {
      eventDispatcher(ENABLE_LOADER);
    }
    return fetch(`${this.apiHost}${url}`, options).then((response) => {
      if (options.method !== "GET") {
        eventDispatcher(SNACK_NOTIFICATION, {
          type: type,
          snackVariant: response.ok ? "success" : "error",
        });
        eventDispatcher(DISABLE_LOADER);
      }
      return response.json();
    });
  }

  public async get<T>(
    url: string,
    type: string,
    params?: Record<string, any>
  ): Promise<T | ErrorResponse> {
    const queryString = params ? this.objectToQueryString(params) : "";
    return this.request<T>(
      `${url}${queryString ? "?" + queryString : ""}`,
      type,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      }
    );
  }

  public async post<T>(
    url: string,
    type: string,
    body: any
  ): Promise<T | ErrorResponse> {
    return this.request<T>(url, type, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  public async put<T>(
    url: string,
    type: string,
    body: any
  ): Promise<T | ErrorResponse> {
    return this.request<T>(url, type, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  public async delete<T>(
    url: string,
    type: string
  ): Promise<T | ErrorResponse> {
    return this.request<T>(url, type, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
    });
  }

  private objectToQueryString(obj: Record<string, any>): string {
    return Object.keys(obj)
      .map(
        (key) => encodeURIComponent(key) + "=" + encodeURIComponent(obj[key])
      )
      .join("&");
  }
}

export default HttpClient;
